import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';

export default function DevicesToolbar({
  onSearchSn,
  onSearchMac,
  onRefresh,
  snValue,
  macValue,
  availableColumns,
  visibleColumns,
  onToggleColumn,
}: {
  onSearchSn?: (q: string) => void;
  onSearchMac?: (q: string) => void;
  onRefresh?: () => void;
  snValue?: string;
  macValue?: string;
  availableColumns?: { key: string; label: string }[];
  visibleColumns?: string[];
  onToggleColumn?: (k: string) => void;
}) {
  const [snInput, setSnInput] = React.useState('');
  const [macInput, setMacInput] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // sync external value (controlled from parent) into internal state so the clear button
  // and input reflect the active filter
  React.useEffect(() => {
    setSnInput(snValue ?? '');
  }, [snValue]);

  React.useEffect(() => {
    setMacInput(macValue ?? '');
  }, [macValue]);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
      <Box sx={{ flex: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          aria-label="refresh"
          size="small"
          variant="contained"
          color="primary"
          endIcon={<RefreshIcon />}
          onClick={() => { if (onRefresh) onRefresh(); }}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
        <Button
          aria-label="columns"
          size="small"
          variant="contained"
          color="primary"
          endIcon={<FilterListIcon />}
          onClick={handleOpen}
          sx={{ textTransform: 'none' }}
        >
          Columns
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose} PaperProps={{ sx: { bgcolor: '#fff' } }}>
          {(availableColumns ?? []).map((c) => {
            const selectedCount = (visibleColumns ?? []).length;
            const isSelected = Boolean(visibleColumns?.includes(c.key));
            const isLast = isSelected && selectedCount <= 1;
            return (
              <MenuItem key={c.key} onClick={() => { if (!isLast) onToggleColumn?.(c.key); }} disabled={isLast}>
                <Checkbox size="small" checked={isSelected} disabled={isLast} />
                <ListItemText sx={{ color: isLast ? 'text.disabled' : 'inherit' }}>{c.label}</ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
        <TextField
          size="small"
          placeholder="Search by SN"
          value={snInput}
          onChange={(e) => setSnInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSearchSn?.(snInput);
          }}
          sx={{ width: 200 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {snInput ? (
                  <IconButton size="small" aria-label="clear sn" onClick={() => { setSnInput(''); onSearchSn?.(''); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton size="small" aria-label="search sn" onClick={() => onSearchSn?.(snInput)}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          placeholder="Search by MAC"
          value={macInput}
          onChange={(e) => setMacInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSearchMac?.(macInput);
          }}
          sx={{ width: 220 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {macInput ? (
                  <IconButton size="small" aria-label="clear mac" onClick={() => { setMacInput(''); onSearchMac?.(''); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton size="small" aria-label="search mac" onClick={() => onSearchMac?.(macInput)}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );
}
